/**
 * EVO — buxgalteriya ekranlarining URL holati.
 *
 * `lib/report-search.ts` bilan bir xil qoida va u yerdagi izoh to'liq amal
 * qiladi: URL ISHONCHSIZ KIRISH, har maydon tozalanadi, default qiymatlar
 * URL ga YOZILMAYDI.
 *
 * Nima uchun alohida fayl: `report-search.ts` `/api/reports/*` ekranlariga
 * tegishli va u boshqa vazifada ham tahrirlanmoqda. Buxgalteriya
 * parametrlari (davr yili, provodka filtri) u yerga aloqasiz — aralashtirish
 * ikkala faylni ham o'qishni qiyinlashtirardi.
 */
import { todayIso } from './format';
import { isIsoDate } from './period';

// ---------------------------------------------------------------------------
// Balans — bitta sana
// ---------------------------------------------------------------------------

export interface BalanceSheetSearch {
  readonly asOf?: string;
}

export function parseBalanceSheetSearch(search: Record<string, unknown>): BalanceSheetSearch {
  return isIsoDate(search.asOf) ? { asOf: search.asOf } : {};
}

/**
 * URL da sana bo'lmasa — BUGUN.
 *
 * `todayIso()` MAHALLIY vaqt zonasida ishlaydi (`format.ts`): `toISOString()`
 * Toshkentda ertalab soat 05:00 gacha KECHAGI sanani berardi va balans bir
 * kunlik provodkalarsiz chiqardi.
 */
export function resolveAsOf(search: BalanceSheetSearch): string {
  return search.asOf ?? todayIso();
}

// ---------------------------------------------------------------------------
// Davrlar — yil
// ---------------------------------------------------------------------------

export interface PeriodsSearch {
  readonly year?: number;
}

export function parsePeriodsSearch(search: Record<string, unknown>): PeriodsSearch {
  const year = Number(search.year);
  // `Number.isInteger` `2026.5` va `NaN` ikkalasini ham rad etadi.
  if (!Number.isInteger(year) || year < 2000 || year > 2100) return {};
  return { year };
}

export function resolveYear(search: PeriodsSearch): number {
  return search.year ?? new Date().getFullYear();
}

// ---------------------------------------------------------------------------
// Provodkalar — sahifa, holat, hisob va davr
// ---------------------------------------------------------------------------

export const JOURNAL_STATUSES = ['draft', 'posted', 'reversed'] as const;
export type JournalStatusFilter = (typeof JOURNAL_STATUSES)[number];

export interface JournalsSearch {
  readonly page?: number;
  readonly status?: JournalStatusFilter;
  /**
   * Hisob bo'yicha filtr — HISOBOTDAN kelinadigan yo'l.
   *
   * P&L satridagi «hisoblar» ro'yxatidan bosilganda ekran aynan shu
   * parametr bilan ochiladi: satr → hisob → provodkalar → manba hujjat
   * (TZ v8.0 §34 «Explainability»). Shuning uchun u URL da bo'lishi SHART —
   * havolani ulashib bo'lsin.
   */
  readonly accountId?: number;
  readonly from?: string;
  readonly to?: string;
}

function isJournalStatus(value: unknown): value is JournalStatusFilter {
  return typeof value === 'string' && (JOURNAL_STATUSES as readonly string[]).includes(value);
}

export function parseJournalsSearch(search: Record<string, unknown>): JournalsSearch {
  const page = Number(search.page);
  const accountId = Number(search.accountId);

  return {
    // 1 — default, shuning uchun URL ga yozilmaydi.
    ...(Number.isInteger(page) && page > 1 ? { page } : {}),
    ...(isJournalStatus(search.status) ? { status: search.status } : {}),
    ...(Number.isInteger(accountId) && accountId > 0 ? { accountId } : {}),
    ...(isIsoDate(search.from) ? { from: search.from } : {}),
    ...(isIsoDate(search.to) ? { to: search.to } : {}),
  };
}
